import { forwardRef, type ReactNode } from 'react';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { Text } from '@dynatrace/strato-components/typography';
import Typography from '@dynatrace/strato-design-tokens/typography';

import {
  type TooltipBaseProps,
  Tooltip,
} from '../../overlays/tooltip/Tooltip.js';
import { KeyboardShortcut } from '../keyboard-shortcut/KeyboardShortcut.js';

// #region Typings

/**
 * Accepted properties for KeyboardShortcutTooltip
 * @public
 */
export interface KeyboardShortcutTooltipProps
  extends TooltipBaseProps,
    StylingProps,
    DataTestId,
    MaskingProps {
  /**
   * The list of keys the component should display, specified in the aria-keyshortcuts format.
   */
  keys: string;

  /**
   * Whether the component should be rendered in a disabled state.
   * @defaultValue 'false'
   */
  shortcutDisabled?: boolean;

  /** Text displayed in the tooltip component. */
  text?: ReactNode;
}
// #endregion Typings

/**
 * Use the `KeyboardShortcutTooltip` component to display a `KeyboardShortcut` along with an optional text inside a tooltip.
 * The `KeyboardShortcutTooltip` opens on hover and click on its trigger element.
 * The trigger element can only be an interactive element, like a button or input.
 * @public
 */
export const KeyboardShortcutTooltip = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  KeyboardShortcutTooltipProps
>((props: KeyboardShortcutTooltipProps, forwardedRef) => {
  const {
    children,
    keys,
    shortcutDisabled,
    text,
    placement = 'top',
    open,
    defaultOpen,
    onOpenChange,
    delay = 'default',
    disabled,
    style: consumerStyle,
    className: consumerClassName,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  return (
    <Tooltip
      ref={forwardedRef}
      placement={placement}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      delay={delay}
      disabled={disabled}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      className={consumerClassName}
      style={consumerStyle}
      text={
        <Flex flexDirection="column" alignItems="center" gap={4}>
          {text && (
            <Text
              textStyle="small-emphasized"
              style={{ maxWidth: '100%', overflowWrap: 'anywhere' }}
            >
              {text}
            </Text>
          )}
          <KeyboardShortcut
            keys={keys}
            variant="minimal"
            disabled={shortcutDisabled}
            style={{
              lineHeight: Typography.Text.Small.Default.LineHeight,
            }}
          />
        </Flex>
      }
      {...remainingProps}
    >
      {children}
    </Tooltip>
  );
});
(
  KeyboardShortcutTooltip as typeof KeyboardShortcutTooltip & {
    displayName: string;
  }
).displayName = 'KeyboardShortcutTooltip';
